import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type UserAuditLogFilters = {
  search: string;
  activityNames: string[];
  startDate: string;
  endDate: string;
  companyId: number | null;
  userIds: number[];
};

export const EMPTY_USER_AUDIT_LOG_FILTERS: UserAuditLogFilters = {
  search: "",
  activityNames: [],
  startDate: "",
  endDate: "",
  companyId: null,
  userIds: [],
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const sanitizeDateFilter = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  const dateOnly = value.slice(0, 10);

  return DATE_ONLY_PATTERN.test(dateOnly) && DateTime.fromISO(dateOnly).isValid
    ? dateOnly
    : "";
};

const sanitizeCompanyIdFilter = (value: unknown): number | null => {
  const companyId = Number(value);

  return Number.isInteger(companyId) && companyId > 0 ? companyId : null;
};

const sanitizeUserIdsFilter = (value: unknown): number[] =>
  Array.isArray(value)
    ? value
        .map(Number)
        .filter((userId) => Number.isInteger(userId) && userId > 0)
    : [];

const sanitizeUserAuditLogFilters = (filters: unknown): UserAuditLogFilters => {
  if (!filters || typeof filters !== "object") {
    return EMPTY_USER_AUDIT_LOG_FILTERS;
  }

  const candidate = filters as Partial<UserAuditLogFilters>;
  const search = typeof candidate.search === "string" ? candidate.search : "";
  const activityNames = Array.isArray(candidate.activityNames)
    ? candidate.activityNames.filter(
        (activityName): activityName is string =>
          typeof activityName === "string" && activityName.length > 0,
      )
    : [];

  return {
    search,
    activityNames,
    startDate: sanitizeDateFilter(candidate.startDate),
    endDate: sanitizeDateFilter(candidate.endDate),
    companyId: sanitizeCompanyIdFilter(candidate.companyId),
    userIds: sanitizeUserIdsFilter(candidate.userIds),
  };
};

export const decodeUserAuditLogFilters = (
  locationSearch: string,
): UserAuditLogFilters => {
  if (!locationSearch || locationSearch === "?fresh") {
    return EMPTY_USER_AUDIT_LOG_FILTERS;
  }

  try {
    return sanitizeUserAuditLogFilters(JSON.parse(decode(locationSearch)));
  } catch {
    return EMPTY_USER_AUDIT_LOG_FILTERS;
  }
};

export const encodeUserAuditLogFilters = (filters: UserAuditLogFilters) =>
  `?${btoa(
    Array.from(new TextEncoder().encode(JSON.stringify(filters)))
      .map((byte) => String.fromCodePoint(byte))
      .join(""),
  )}`;

const decode = (locationSearch: string) =>
  new TextDecoder().decode(
    Uint8Array.from(atob(locationSearch.slice(1)), (char) =>
      char.charCodeAt(0),
    ),
  );

export const isUserAuditLogFiltersEmpty = ({
  search,
  activityNames,
  startDate,
  endDate,
  companyId,
  userIds,
}: UserAuditLogFilters) =>
  search.length === 0 &&
  activityNames.length === 0 &&
  startDate.length === 0 &&
  endDate.length === 0 &&
  companyId === null &&
  userIds.length === 0;

export const useUserAuditLogFilters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(
    () => decodeUserAuditLogFilters(location.search),
    [location.search],
  );

  const setFilters = useCallback(
    (nextFilters: UserAuditLogFilters) => {
      const sanitizedFilters = sanitizeUserAuditLogFilters(nextFilters);
      const query = isUserAuditLogFiltersEmpty(sanitizedFilters)
        ? ""
        : encodeUserAuditLogFilters(sanitizedFilters);

      navigate(`${location.pathname}${query}`, { replace: true });
    },
    [location.pathname, navigate],
  );

  return {
    filters,
    setFilters,
  };
};
